import { SceneContainer } from "GameObjects/StoryGame/SceneContainer";
import { SceneSprite } from "GameObjects/StoryGame/SceneSprite";
import { Elastic, Expo, TimelineLite, TweenLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "Singletons/Application";
import { AbstractStoryState } from "States/AbstractStoryState";
import { changeParent } from "Utils/layoutUtils";
import { list } from "Utils/Reflection";

import { hh_ch2_72dpi_p050607 } from "../../../layouts/ch2_p05/hh_ch2_72dpi_p05-06-07"

export class Chapter2Page5 extends AbstractStoryState {

    readonly panels = [
        this.addChild(new SceneContainer()),
        this.addChild(new SceneContainer()),
        this.addChild(new SceneContainer()),
    ];

    readonly bg = list((i) => this.panels[i].addChildAt(new SceneSprite(), 0));
    readonly bubbleStart = list((i) => this.panels[i].addChildAt(new SceneSprite(), 1));
    readonly bubbleReveal = list((i) => this.panels[i].addChildAt(new SceneSprite(), 1));
    readonly chair = list((i) => this.panels[i].addChildAt(new SceneSprite(), 1));

    readonly amy = this.panels[0].addChild(new SceneSprite()); // temp, will be spine
    readonly piper = this.panels[1].addChild(new SceneSprite()); // temp, will be spine
    readonly oscar = this.panels[2].addChild(new SceneSprite()); // temp, will be spine

    readonly frameLeft = this.addChild(SceneSprite.withPrefix("ch2_p05/"));
    readonly frameRight = this.addChild(SceneSprite.withPrefix("ch2_p05/"));

    public init(pSize: PIXI.Point, pData: any) {
        // exported layout from Animate CC
        hh_ch2_72dpi_p050607.call(this);
		this.frameLeft.visible = false;
		this.frameRight.visible = false;

        this.panels[1].visible = false;
        this.panels[2].visible = false;
        // hide the hidden images
        this.bubbleReveal.forEach(b=>b.visible = false);

        // super.init calls onResize
        super.init(pSize, pData);
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
    }

    public onAnimateInComplete() {
        super.onAnimateInComplete();
        // interaction
        this.makeScratchable(
            this.bubbleStart[0],
            this.bubbleReveal[0],
            "HH_Ch2_ThoughtBubbleScratch1_FX",
            "HH_Ch2_AmyThoughtBubble_FX",
            this.onPanel1ScratchComplete.bind(this),
        );
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
    }

    makeScratchable(
        initialImage: PIXI.Sprite,
        hiddenImage: PIXI.Sprite,
        scratchSound: string,
        revealSound: string,
        onComplete: () => void,
    ) {
        const threshold = 0.75;

        const sound = HLF.Application.instance.audio.createAudioTrack(scratchSound, HLF.AudioCategory.SFX);
        const sound2 = HLF.Application.instance.audio.createAudioTrack(revealSound, HLF.AudioCategory.SFX);

        // make rendertexture same size as hidden image
        const renderTexture = PIXI.RenderTexture.create({
            width: hiddenImage.width,
            height: hiddenImage.height,
            resolution: 0.25,
        });

        // position mask over image, but don't use mask.anchor
        const mask = this.addChild(new PIXI.Sprite(renderTexture));
        mask.position.set(
            initialImage.position.x - (initialImage.width * initialImage.anchor.x),
            initialImage.position.y - (initialImage.height * initialImage.anchor.y),
        );

        // "soft edge" brush
        const brush = new PIXI.Graphics();
        brush.beginFill(0xff0000, 0.2);
        brush.drawCircle(0, 0, 50);
        brush.beginFill(0xff0000, 0.6);
        brush.drawCircle(0, 0, 40);
        brush.beginFill(0xff0000);
        brush.drawCircle(0, 0, 20);
        brush.endFill();

        // count pixels before applying mask
        hiddenImage.visible = true;
        const targetPercent = this.percentOpaquePixels(hiddenImage);
        hiddenImage.mask = mask;

        const throttle = 0.100; // seconds after stop scrubbing to check progress

        const complete = () => {
            initialImage.removeListener("pointermove", pointerMoveCallback);
            initialImage.removeListener("pointerdown", pointerDownCallback);
            throttledCheckProgress?.kill();
            hiddenImage.mask = null;
            mask.destroy();
            renderTexture.destroy();
            sound.stop();
            sound2.fadeTo(0, 500);
            TweenLite.delayedCall(0.5, onComplete);
        }

        const checkProgress = () => {
            // counting pixels in mask is easier than in the masked image
            const revealedPercent = this.percentOpaquePixels(renderTexture);
            if ((revealedPercent / targetPercent) >= threshold) {
                complete();
            }
            console.log(Math.round(revealedPercent / targetPercent * 100) + "%");
            sound.pause();
        }

        const throttledCheckProgress = TweenLite.delayedCall(throttle, checkProgress).pause();

        const paint = (pos: { x: number, y: number }) => {
            brush.position.copyFrom(pos);
            Application.instance.renderer.render(brush, renderTexture, false, undefined, false);
        }

        const pointerDownCallback = (e: PIXI.InteractionEvent) => {
            paint(e.data.getLocalPosition(mask));
            if (!sound.isPlaying()) {
                sound.play();
            }
            if (!sound2.isPlaying()) {
                sound2.setLooped(true);
                sound2.play();
                sound2.setVolume(0);
                sound2.fadeTo(1, 5000);
            }
        }

        const pointerMoveCallback = (e: PIXI.InteractionEvent) => {
            // const localPos = e.data.getLocalPosition(hiddenImage);
            // console.log(localPos);
            if (hiddenImage.containsPoint(e.data.global)) {
                paint(e.data.getLocalPosition(mask));
                throttledCheckProgress.restart(true); // check progress 100ms after stop moving cursor
                if (!sound.isPlaying()) {
                    sound.play();
                }
                if (!sound2.isPlaying()) {
                    sound2.setLooped(true);
                    sound2.play();
                    sound2.setVolume(0);
                    sound2.fadeTo(1, 5000);
                }
            }
        }

        const pointerUpCallback = (e: PIXI.InteractionEvent) => {
            throttledCheckProgress.restart(true); // check progress 100ms after stop moving cursor
            sound.pause();
        }

        initialImage.interactive = true;
        initialImage.addListener(HLF.Input.Events.POINTER_MOVE, pointerMoveCallback);
        initialImage.addListener(HLF.Input.Events.POINTER_DOWN, pointerDownCallback);

        initialImage.addListener(HLF.Input.Events.POINTER_UP, pointerUpCallback);
        initialImage.addListener(HLF.Input.Events.POINTER_UP_OUTSIDE, pointerUpCallback);
        initialImage.addListener(HLF.Input.Events.POINTER_CANCEL, pointerUpCallback);
        initialImage.addListener(HLF.Input.Events.POINTER_OUT, pointerUpCallback);
    }

    percentOpaquePixels(image: PIXI.Sprite | PIXI.RenderTexture) {

        if (image instanceof PIXI.RenderTexture) {
            if (image.baseTexture === null) {
                return 0;
            }
        }

        const pixels = Application.instance.renderer.extract.pixels(image);

        const minOpacity = 0 / 255;

        let sum = 0;
        // array goes [red, green, blue, alpha, red, green, blue, alpha, ... etc]
        for (let i = 3; i < pixels.length; i += 4) {
            if (pixels[i] > minOpacity) {
                sum++;
            }
        }

        const length = (pixels.length / 4);
        const percent = sum / length
        return percent;
    }

    protected onPanel3ScratchComplete() {
        HLF.Application.instance.audio.stop("HH_Ch2_OscarThoughtBubble_FX", HLF.AudioCategory.SFX);
        this.gotoNextState({ transition: "up" });
    }

    protected onPanel2ScratchComplete() {
        this.panels[1].FadeOut();
        this.panels[2].FadeIn();
        HLF.Application.instance.audio.stop("HH_Ch2_PiperThoughtBubble_FX", HLF.AudioCategory.SFX);
        this.makeScratchable(
            this.bubbleStart[2],
            this.bubbleReveal[2],
            "HH_Ch2_ThoughtBubbleScratch1_FX",
            "HH_Ch2_OscarThoughtBubble_FX",
            this.onPanel3ScratchComplete.bind(this),
        );
    }

    protected onPanel1ScratchComplete() {
        this.panels[0].FadeOut();
        this.panels[1].FadeIn();
        HLF.Application.instance.audio.stop("HH_Ch2_AmyThoughtBubble_FX", HLF.AudioCategory.SFX);
        this.makeScratchable(
            this.bubbleStart[1],
            this.bubbleReveal[1],
            "HH_Ch2_ThoughtBubbleScratch2_FX",
            "HH_Ch2_PiperThoughtBubble_FX",
            this.onPanel2ScratchComplete.bind(this),
        );
    }
}